import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import {
  PpFormField,
  PpGroupedValueItem,
  PpValueItem,
} from '../types/forms.type';
import _ from 'underscore';
import { PpHelper } from '../utils/helper';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class FormsService {
  constructor() {}

  /**
   *
   * @param formFields :CanFormField[]
   * @param values :object
   *
   * Parse upload value
   */
  parseUploadValue(
    formFields: PpFormField[],
    values: any,
    form: FormGroup,
  ): void {
    formFields.forEach((formField) => {
      if (formField.type === 'group' && formField.formFields?.length) {
        const fg = form.controls[formField.name] as FormGroup;
        // Parse group type form to select value from file uploads
        this.parseUploadValue(formField.formFields, values[formField.name], fg);
      } else if (formField.type === 'array' && formField.formFields?.length) {
        const fa = form.controls[formField.name] as FormArray;
        // Parse group type form to select value from file uploads
        if (values[formField.name]?.length) {
          values[formField.name].forEach((value: any, index: number) => {
            const fg = fa.controls[index] as FormGroup;
            this.parseUploadValue(formField.formFields!, value, fg);
          });
        }
      } else {
        if (
          (formField.type === 'image' || formField.type === 'document') &&
          values[formField.name]
        ) {
          if (_.isArray(values[formField.name])) {
            values[formField.name].forEach((value: any, index: number) => {
              if (value.type === 'upload') {
                values[formField.name][index] = value.value;
              }
            });
          } else {
            if (values[formField.name]?.type === 'upload') {
              const uploadValue = values[formField.name].value;
              values[formField.name] = uploadValue;
            }
          }
        }
      }
    });
  }

  getAllEditedValues(form: FormGroup) {
    let dirtyValues: any = {};

    Object.keys(form.controls).forEach((key) => {
      const currentControl: any = form.controls[key];

      if (currentControl.dirty) {
        if (currentControl['controls'])
          dirtyValues[key] = this.getAllEditedValues(
            currentControl as FormGroup,
          );
        else dirtyValues[key] = currentControl.value;
      }
    });

    return dirtyValues;
  }

  getImageViewValue(element: PpFormField) {
    let imageValue;
    if (element.file?.src && element.file?.viewValue) {
      if (element.file.src?.dataSource && element.file.src?.srcDataKey) {
        imageValue = PpHelper.getValueWithApiKey(
          element.file.src.dataSource,
          element.file.src.srcDataKey,
          element.file.src.srcKeySeparator!,
          true,
        );
      } else if (element.file.src?.srcForImage) {
        // In case user want to give image url directly
        imageValue = element.file.src.srcForImage;
      }
    }
    return !PpHelper.isEmpty(imageValue) ? imageValue : undefined;
  }

  setUploadValueInNestedForm(defaultValue: any, formFields?: PpFormField[]) {
    formFields?.map((field) => {
      if (field.type === 'image' || field.type === 'document') {
        if (field.file?.multipleSelect) {
          defaultValue[field.name] = defaultValue[field.name].map(
            (defValue: any) => {
              return this.setUploadValue(defValue, field.file);
            },
          );
        } else {
          defaultValue[field.name] = this.setUploadValue(
            defaultValue[field.name],
            field.file,
          );
        }
      }
    });
  }

  setUploadValue(defaultValue: any, file: any) {
    let viewValue: string;
    if (typeof defaultValue === 'string') {
      viewValue = defaultValue;
    } else {
      viewValue = PpHelper.getValueWithApiKey(
        { data: defaultValue },
        file?.viewValue?.apiViewValueKey!,
        file?.viewValue?.viewValueKeySeparator,
        true,
      );
    }
    return {
      value: defaultValue,
      type: 'upload',
      viewValue: viewValue,
    };
  }

  findFormField(formFields: PpFormField[], name: string): any {
    for (let index = 0; index < formFields.length; index++) {
      const element = formFields[index];
      if (element.name === name) {
        return element;
      } else {
        if (element.formFields) {
          const found = this.findFormField(element.formFields, name);

          if (found) {
            return found;
          }
        }
      }
    }
  }

  /**
   *
   * @param name : string
   * Set Relative Form Field Value
   */
  setFormFieldValue(name: string, value: any, form: FormGroup) {
    // Return false in case of empty
    if (!name) {
      return false;
    }
    // Storing the nested key in array
    const keys = name.split('.');
    // Check whether key is in present in form controls
    if (keys[0] in form.controls) {
      // Check key length
      if (keys.length === 1) {
        // Set value
        form.controls[keys[0]].setValue(value);
        form.controls[keys[0]].markAsDirty();
      } else {
        let fg: any;
        // Init form
        fg = form.controls[keys[0]];
        // Loop keys
        for (let i = 1; i < keys.length; i++) {
          // Check for controls and keys
          if ('controls' in fg && keys[i] in fg.controls) {
            // Check for last index
            if (i === keys.length - 1) {
              // Set value
              fg.controls[keys[i]].setValue(value);
              form.controls[keys[0]].markAsDirty();
            } else {
              // Set form
              fg = fg.controls[keys[i]];
            }
          } else {
            return false;
          }
        }
      }
    } else {
      return false;
    }
    return false;
  }

  /**
   *
   * @param formFields :CanFormField[]
   * @param values :object
   *
   * Remove values that are not required to send
   */
  removeNotIncludedValues(
    formFields: PpFormField[],
    values: any,
    form: FormGroup,
  ): void {
    // Loop Form Fields
    formFields.forEach((formField) => {
      // Check for notIncluded in formField
      if (
        formField.send === 'notSend' ||
        (formField.send === 'sendOnModified' &&
          !form.controls[formField.name].dirty)
      ) {
        // Delete from object
        delete values[formField.name];
      } else {
        // Check for type 'group'
        if (
          formField.type === 'group' &&
          formField.formFields &&
          formField.formFields.length
        ) {
          const fg = form.controls[formField.name] as FormGroup;
          // Remove values from group that are not required to send
          this.removeNotIncludedValues(
            formField.formFields,
            values[formField.name],
            fg,
          );
        } else if (
          formField.type === 'array' &&
          formField.formFields &&
          formField.formFields.length
        ) {
          const fa = form.controls[formField.name] as FormArray;
          // Remove values from array that are not required to send
          if (values[formField.name] && values[formField.name].length) {
            values[formField.name].forEach((value: any, index: number) => {
              const fg = fa.controls[index] as FormGroup;
              this.removeNotIncludedValues(formField.formFields!, value, fg);
            });
          }
        }
      }
    });
  }

  /**
   *
   * @param data :Array<object>
   * @param key :string
   * @param value
   */
  getObjectFromArray(data: Array<object>, key: string, value: any) {
    if (data && data.length) {
      if (_.isArray(value)) {
        return data.filter((val: any) =>
          value.includes(this.getValueByPath(val, key)),
        );
      } else {
        // Find value index
        const index = data.findIndex(
          (val: any) => this.getValueByPath(val, key) === value,
        );
        // If value present
        if (index > -1) {
          // Return value
          return data[index];
        }
      }
    }
    return null;
  }

  getValueByPath(obj: any, path: string) {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }

  /**
   *
   * @param formFields :CanFormField
   * Set value has been changed in form field
   */
  setFormControlDirty(formField: PpFormField, form: FormGroup): void {
    form.controls[formField.name].markAsDirty();
  }

  /**
   *
   * @param data:object
   * @param key:string
   *
   * Fetching Value with API KEY
   */
  getValueWithApiKey(data: any, key: string, separators?: Array<string>): any {
    if (_.isArray(data)) {
      return data.map((d) => {
        const valueWithApiKey = PpHelper.getValueWithApiKey(d, key, separators);
        return PpHelper.isEmpty(valueWithApiKey) ? undefined : valueWithApiKey;
      });
    } else {
      const valueWithApiKey = PpHelper.getValueWithApiKey(
        data,
        key,
        separators,
      );
      return PpHelper.isEmpty(valueWithApiKey) ? undefined : valueWithApiKey;
    }
  }

  getValueWithApiKeySecondary(data: any, viewValueKey: any): any {
    if (_.isArray(data)) {
      return data.map((d) => {
        const valueWithApiKey = PpHelper.getValueWithApiKeySecondary(
          d,
          viewValueKey,
        );
        return PpHelper.isEmpty(valueWithApiKey) ? undefined : valueWithApiKey;
      });
    } else {
      const valueWithApiKey = PpHelper.getValueWithApiKeySecondary(
        data,
        viewValueKey,
      );
      return PpHelper.isEmpty(valueWithApiKey) ? undefined : valueWithApiKey;
    }
  }

  /**
   *
   * @param control
   * @returns current selected values
   */
  getSelectedValues(control: PpFormField): string[] {
    const selectedValue = control.formControl?.value;
    let values: string[] = [];
    if (control.multipleSelect && selectedValue?.length) {
      values = [...selectedValue];
    } else if (!control.multipleSelect && selectedValue) {
      values = [selectedValue];
    }
    return values;
  }

  makePreviewObject(globalFormFields: PpFormField[], resultObj: any = []) {
    [...globalFormFields].forEach(
      (
        {
          type,
          placeholder,
          multipleSelect,
          formControl,
          isGrouped,
          values,
          date,
          groupedValues,
          formFields,
        }: PpFormField,
        index: number,
      ) => {
        switch (type) {
          case 'text':
          case 'textarea':
          case 'number':
          case 'password':
          case 'rich-textarea': {
            if (formControl?.value) {
              resultObj.push({
                type,
                index,
                placeholder,
                value: formControl?.value,
                pristine: formControl?.pristine,
              });
            }
            break;
          }
          case 'select':
          case 'multicheckbox':
          case 'tags':
          case 'radio': {
            let value;
            if (isGrouped && type === 'multicheckbox') {
              value = formControl?.value
                ? JSON.parse(JSON.stringify(groupedValues))
                    ?.map((group: PpGroupedValueItem) => {
                      group.values = group.values.filter(
                        (subValue: PpValueItem) =>
                          formControl?.value.indexOf(subValue.value) !== -1,
                      );
                      return group;
                    })
                    .filter(
                      (subValue: PpGroupedValueItem) =>
                        subValue.values.length > 0,
                    )
                : undefined;
            } else if (
              multipleSelect ||
              type === 'tags' ||
              type === 'multicheckbox'
            ) {
              value = formControl?.value
                ? formControl?.value.map((selectedValue: any) => {
                    return values?.find(
                      (value) => value?.value === selectedValue,
                    )?.viewValue;
                  })
                : undefined;
            } else {
              value = formControl?.value
                ? values?.find((value) => value?.value === formControl?.value)
                    ?.viewValue
                : undefined;
            }
            if (value) {
              resultObj.push({
                type,
                index,
                placeholder,
                value,
                multipleSelect,
                isGrouped,
                pristine: formControl?.pristine,
              });
            }
            break;
          }
          case 'checkbox':
          case 'toggle': {
            resultObj.push({
              type,
              index,
              placeholder,
              value: formControl?.value ? 'Yes' : 'No',
              pristine: formControl?.pristine,
            });
            break;
          }
          case 'date':
          case 'time': {
            const format =
              type === 'date' ? (date?.enabledTime ? 'LLL' : 'll') : 'LT';

            let value;
            if (date?.enabledRange || date?.dateRange) {
              value = [
                moment(formControl?.value[0]).format(format),
                moment(formControl?.value[1]).format(format),
              ];
            } else {
              value = moment(formControl?.value).format(format);
            }
            if (formControl?.value) {
              resultObj.push({
                type,
                index,
                placeholder,
                value,
                pristine: formControl?.pristine,
                isRange: date?.enabledRange || date?.dateRange,
              });
            }
            break;
          }
          case 'autocomplete': {
            break;
          }
          case 'image':
          case 'document': {
            if (formControl?.value?.viewValue) {
              resultObj.push({
                type,
                index,
                placeholder,
                value: formControl?.value?.viewValue,
                multipleSelect,
                pristine: formControl?.pristine,
              });
            }
            break;
          }
          case 'group': {
            const value = this.makePreviewObject(formFields!);
            if (value.length > 0) {
              resultObj.push({
                type,
                index,
                placeholder,
                value,
              });
            }
            break;
          }
          case 'array': {
            const formFields: any = [];
            const groups = (formControl as any).controls;
            groups.map((group: any) => {
              formFields.push(group.formFields);
            });
            const value = formFields
              .map((arrField: any) => this.makePreviewObject(arrField))
              .filter((subArray: any) => subArray.length > 0);
            if (value.length > 0) {
              resultObj.push({
                type,
                index,
                placeholder,
                value,
              });
            }
            break;
          }
        }
      },
    );

    return resultObj;
  }

  public static deepClone(thing: any, opts: { newFns?: boolean } = {}): any {
    if (thing instanceof Array) {
      return thing.map(function (i) {
        return FormsService.deepClone(i, opts);
      });
    } else if (thing instanceof Date) {
      return new Date(thing);
    } else if (thing instanceof RegExp) {
      return new RegExp(thing);
    } else if (thing instanceof Function) {
      return opts && opts.newFns
        ? new Function('return ' + thing.toString())()
        : thing;
    } else if (thing instanceof Object) {
      if (thing?.constructor?.name === 'Object') {
        let newObject: any = {};
        Object.keys(thing).forEach(function (key) {
          newObject[key] = FormsService.deepClone(thing[key], opts);
        });
        return newObject;
      }
      return thing;
    } else if ([undefined, null].indexOf(thing) > -1) {
      return thing;
    } else {
      if (thing.constructor.name === 'Symbol') {
        return Symbol(
          thing
            .toString()
            .replace(/^Symbol\(/, '')
            .slice(0, -1),
        );
      }
      return thing.__proto__.constructor(thing);
    }
  }
}
